import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PropTypes from "prop-types";
import "../client-dashboard/clientDashboardMap.css";
import "./HeatMapMapPublic.css";
import "leaflet.heat";
import HeatmapOverlay from "leaflet-heatmap";
import GradientLegend from "./cilent-dashboard-util/GradientLegend";
import LoadingBar from '../backend/components/LoadingBar';
import { Icon } from "leaflet";

import { showInfoTooltip } from "./cilent-dashboard-util/GradientLegend"

// Assets
import fnnLogo from "./client-dashboard-assets/FNNLogoWithBlackFont-06.png"
import fireMarker from './client-dashboard-assets/flames-icon.png'

const createFireIcon = (size) =>
  new Icon({
    iconUrl: fireMarker,
    iconSize: [size, size * 1.2], // taller than wide
  });

async function heatMapDataFromApi() {
  try {
    const response = await fetch("/api/fl_lightning_ignited_fires/", {
      method: "GET",
    });

    if (!response.ok) {
      console.error("Failed to fetch lightning data:", response.statusText);
      return [];
    }

    const data = await response.json();

    // data keys are JSON stringified polygons; values are counts
    return Object.entries(data).map(([rangeKey, count]) => {
      const coords = JSON.parse(rangeKey); // coords is array of [lon, lat]

      // Calculate center point (midpoint) of the polygon bounding box
      const lons = coords.map((c) => c[0]);
      const lats = coords.map((c) => c[1]);
      const midLon = (Math.min(...lons) + Math.max(...lons)) / 2;
      const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;

      return [midLon, midLat, count]; // Leaflet Heat expects [lng, lat, intensity]
    });
  } catch (error) {
    console.error("Error fetching lightning data:", error);
    return [];
  }
}

// --- HeatMapLayer Component ---
export const HeatMapLayer = ({ data, onExtremaChange }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const cfg = {
      radius: 0.125,
      maxOpacity: 0.7,
      minOpacity: 0.1,
      scaleRadius: true,
      useLocalExtrema: true,
      latField: "lat",
      lngField: "lng",
      valueField: "count",
      onExtremaChange: (data) => {
        onExtremaChange(data.min, data.max);
      },
    };

    const heat = new HeatmapOverlay(cfg);
    heat.setData({
      max: Math.max(...data.map((d) => d[2])),
      data: data.map(([lng, lat, count]) => ({ lat, lng, count })),
    });

    map.addLayer(heat);
    return () => {
      map.removeLayer(heat);
    };
  }, [map, data, onExtremaChange]);

  return null;
};

HeatMapLayer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  onExtremaChange: PropTypes.func.isRequired,
};

// --- Main Heat Map Component ---
export const HeatMapMapPublic = () => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [loading, setLoading] = useState(true); // ✅ track loading
  const [firepoints, setFirepoints] = useState([]);
  const [zoom, setZoom] = useState(7); // initial zoom
  const [showFirepoints, setShowFirepoints] = useState(false); // ✅ new toggle state
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await heatMapDataFromApi();
      setHeatmapData(data);
      setLoading(false);
    };

    // Initial load
    loadData();

    // ✅ Refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  // Fetch Florida FirePoints every 5 minutes
  useEffect(() => {
    const fetchFirepoints = async () => {
      try {
        const res = await fetch(
          "/portal/api/firepoints/?region=detectors_florida",
          { method: "GET" }
        );
        const data = await res.json();
        if (data.length && data[0].coordinates) {
          setFirepoints(data[0].coordinates);
          setEarliestDate(data[0].earliest_date || null);
          setLatestDate(data[0].latest_date || null);
        }
      } catch (err) {
        console.error("Error fetching firepoints:", err);
      }
    };

    // Initial fetch
    fetchFirepoints();

    // Set interval to fetch every 5 minutes
    const interval = setInterval(fetchFirepoints, 5 * 60 * 1000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US"); // e.g., "3/11/2025"
  };

  // Component to track map zoom and update state
  const ZoomListener = () => {
    const map = useMap();
    map.on("zoomend", () => {
      setZoom(map.getZoom());
    });
    return null;
  };

  // Compute marker size based on zoom
  const getMarkerSize = () => Math.min(5 + zoom * 1.5, 7 + zoom * 1.5); // tweak as needed

  const center = [28.44146, -81.40263];

  const handleExtremaChange = (newMin, newMax) => {
    setMin(newMin);
    setMax(newMax);
  };

  return (
    <div className="public-heatmap-wrapper">

      <MapContainer center={center} zoom={7} minZoom={2} style={{ height: "100vh", width: "100%" }}>
        <div className='public-heatmap-nav'>
          <img
            onClick={() => window.open('https://fireneuralnetwork.com/', '_blank', 'noopener,noreferrer')}
            style={{ cursor: "pointer" }} className="fnn-header-logo" src={fnnLogo} alt="" />
          <h1>Florida Live Lightning Tracker</h1>
        </div>


        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        {/* Heatmap */}
        <HeatMapLayer data={heatmapData} onExtremaChange={handleExtremaChange} />

        {/* Listen to zoom */}
        <ZoomListener />

        {/* FirePoints Markers */}
        {showFirepoints &&
          firepoints.map((coord, idx) => (
            <Marker
              key={idx}
              position={[coord.latitude, coord.longitude]}
              icon={createFireIcon(getMarkerSize())}
            />
          ))}

      </MapContainer>

      <div className="fire-info-public-wrapper">

        <div className="fire-info-public fire-with-num">
          <div className="fire-info-title">
            2025 Florida Lightning Ignited Fires
            <span style={{ paddingLeft: '0.2rem' }}>
              {showInfoTooltip(
                `Lightning Ignited Fires between ${formatDate(earliestDate)} and ${formatDate(latestDate)}`
              )}
            </span>
          </div>
          <h1>{firepoints.length}</h1>
        </div>

        <div
          className="fire-info-public"
          onClick={(e) => {
            setShowFirepoints(!showFirepoints);
          }}
          style={{ cursor: "pointer" }}
        >
          <img
            src={fireMarker}
            alt="fire-img"
            className={showFirepoints ? "fire-icon-on" : "fire-icon-off"}
            title={showFirepoints ? "Hide FirePoints" : "Show FirePoints"}
          />
          Click to see the Fires
        </div>
      </div>

      {/* ✅ Legend with loading + no data handling */}
      {(min >= 0 && max > 0) ? (
        <div className="lightning-info-legend lightning-info-public">
          <div className="lightning-info-legend-inner">
            <GradientLegend
              min={1}
              max={max}
              title="Lightning Strikes"
              tooltip="This map is showing Cloud-to-Ground strikes detected in the past 4 hours."
            />
          </div>
        </div>
      ) : (
        <div className="lightning-info-legend lightning-info-public">
          {loading ? (
            <LoadingBar />
          ) : (
            <div className="no-lightning-data">
              <p>No⚡data available.</p>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default HeatMapMapPublic;
